var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Button, Card, CardContent, Checkbox, List, ListItem, ListItemAvatar, ListItemButton, ListItemText, Typography, } from "@mui/material";
import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useRetrievePlayersByTeamId, useRetrieveTeam, useRetrieveUserTeamPermissions, useSetAssistantManagers, } from "../ApiProvider";
import { ErrorView } from "../nav/Error";
import { Forbidden } from "../nav/Forbidden";
import { Loading } from "../nav/Loading";
import { PlayerAvatar } from "../players/PlayerAvatar";
import { playerDisplayName } from "../players/PlayerDisplayNameLink";
import ErrorSnackbar from "../utils/ErrorSnackbar";
import { useNumericParams } from "../utils/useNumericParams";
export var ManageAssistantManagers = function () {
    var id = useNumericParams().id;
    var navigate = useNavigate();
    // null until the user changes something; then it holds their working selection.
    var _a = __read(useState(null), 2), selected = _a[0], setSelected = _a[1];
    var _b = __read(useState(""), 2), errorMessage = _b[0], setErrorMessage = _b[1];
    var _c = useRetrieveUserTeamPermissions({ teamId: id }), permissions = _c.data, permsLoading = _c.isLoading, permsError = _c.error;
    var _d = permissions || {}, _e = _d.canView, canView = _e === void 0 ? false : _e, _f = _d.canModify, canModify = _f === void 0 ? false : _f;
    var _g = useRetrieveTeam({ id: id, enabled: canView }), team = _g.data, teamLoading = _g.isLoading, teamError = _g.error;
    var _h = useRetrievePlayersByTeamId({ teamId: id, enabled: canView }), players = _h.data, playersLoading = _h.isLoading, playersError = _h.error;
    var setMutation = useSetAssistantManagers({
        teamId: id,
        onSuccess: function () { return navigate("/teams/".concat(id, "/")); },
        onError: function () { return setErrorMessage("Failed to update assistant managers."); },
    });
    // Eligible = active registered members (have a user account) who aren't the
    // manager. Inactive/removed members can't be appointed.
    var eligible = useMemo(function () {
        return (players || [])
            .filter(function (p) { var _a; return p.isActive && ((_a = p.user) === null || _a === void 0 ? void 0 : _a.pk) != null && p.user.pk !== (team === null || team === void 0 ? void 0 : team.manager); })
            .map(function (p) { return ({ player: p, userId: p.user.pk }); });
    }, [players, team === null || team === void 0 ? void 0 : team.manager]);
    var current = useMemo(function () { var _a; return new Set((_a = team === null || team === void 0 ? void 0 : team.assistantManagers) !== null && _a !== void 0 ? _a : []); }, [team === null || team === void 0 ? void 0 : team.assistantManagers]);
    var working = selected !== null && selected !== void 0 ? selected : current;
    if (permsLoading || teamLoading || playersLoading) {
        return _jsx(Loading, {});
    }
    if (permsError || teamError || playersError) {
        return (_jsx(ErrorView, { error: permsError ||
                teamError ||
                playersError ||
                new Error("Failed to load.") }));
    }
    // Managing assistant managers is manager-only.
    if (!canView || !canModify) {
        return _jsx(Forbidden, {});
    }
    var toggle = function (userId) {
        var next = new Set(working);
        if (next.has(userId)) {
            next.delete(userId);
        }
        else {
            next.add(userId);
        }
        setSelected(next);
    };
    var dirty = selected != null &&
        (selected.size !== current.size ||
            __spreadArray([], __read(selected), false).some(function (uid) { return !current.has(uid); }));
    return (_jsxs(React.Fragment, { children: [_jsxs(Box, { sx: { mb: 3 }, children: [_jsx(Typography, { variant: "h4", sx: { mb: 1 }, children: "Assistant Managers" }), _jsxs(Typography, { variant: "body1", color: "text.secondary", children: ["Select team members who can help enter", " ", _jsx(Link, { to: "/teams/".concat(id, "/"), style: { textDecoration: "none" }, children: team === null || team === void 0 ? void 0 : team.name }), "'s match stats. Assistant managers can enter stats only \u2014 they can't edit the team, roster, or transfers."] })] }), _jsx(Card, { children: _jsxs(CardContent, { children: [eligible.length === 0 ? (_jsx(Box, { sx: { textAlign: "center", py: 4 }, children: _jsx(Typography, { variant: "body1", color: "text.secondary", children: "No eligible members. Add registered players to the team first \u2014 only members with an account can be assistant managers." }) })) : (_jsx(List, { disablePadding: true, children: eligible.map(function (_a) {
                                var player = _a.player, userId = _a.userId;
                                return (_jsx(ListItem, { disablePadding: true, secondaryAction: _jsx(Checkbox, { edge: "end", checked: working.has(userId), onChange: function () { return toggle(userId); }, inputProps: {
                                            "aria-label": "Assistant manager: ".concat(playerDisplayName(player)),
                                        } }), children: _jsxs(ListItemButton, { onClick: function () { return toggle(userId); }, children: [_jsx(ListItemAvatar, { sx: { mr: 2 }, children: _jsx(PlayerAvatar, { player: player, size: 40 }) }), _jsx(ListItemText, { primary: playerDisplayName(player) })] }) }, player.id));
                            }) })), _jsxs(Box, { sx: {
                                mt: 3,
                                display: "flex",
                                justifyContent: "flex-end",
                                gap: 1,
                            }, children: [_jsx(Button, { component: Link, to: "/teams/".concat(id, "/"), variant: "outlined", children: "Cancel" }), _jsx(Button, { variant: "contained", disabled: !dirty || setMutation.isLoading, onClick: function () { return setMutation.mutate(__spreadArray([], __read(working), false)); }, children: "Save" })] })] }) }), _jsx(ErrorSnackbar, { open: !!errorMessage, message: errorMessage, onClose: function () { return setErrorMessage(""); } })] }));
};
